import type { RequestFn, RequestOptions } from '../client'
import { fetchAllPages, type AllPages, type FetchAllPagesOptions } from '../pagination'
import type { QueryParams } from '../query'
import type { ListEnvelope, StoryGroupRow, StoryRow, StoryTagRow } from '../types'

export const STORIES_PATH = '/v3/newsdesk/stories'
export const STORY_GROUPS_PATH = '/v3/newsdesk/stories/groups'
export const STORY_TAGS_PATH = '/v3/newsdesk/stories/tags'

/** per_page above this is a 422. */
export const STORIES_PER_PAGE_MAX = 400

export const STORY_DATE_WINDOWS = ['today', 'tomorrow', 'today_and_tomorrow', 'from_today', 'from_tomorrow'] as const
export type StoryDateWindow = (typeof STORY_DATE_WINDOWS)[number]

export const STORY_INCLUDES = ['assignments', 'tasks', 'comments', 'attachments', 'media_assets', 'share_requests', 'scripts', 'distributions'] as const
export type StoryInclude = (typeof STORY_INCLUDES)[number]

/** Columns that take filter[<column>] and group_by=<column>. */
export const STORY_FILTERABLE_COLUMNS = [
  'title',
  'story_type',
  'pillar',
  'story_approved',
  'status',
  'source',
  'live_type',
  'live_source',
  'location',
  'project_folder',
  'geocode_zip',
  'promote_date_mode',
  'created_by_user_id',
  'pitched_by_user_id',
  'parent_id',
  'shared_story_id',
  'is_privatized',
  'is_posted',
  'weight'
] as const
export type StoryFilterableColumn = (typeof STORY_FILTERABLE_COLUMNS)[number]

/**
 * A column filter value. One value is a case-insensitive equals, a list is IN,
 * and null or '' is IS NULL.
 */
export type ColumnFilterValue = string | number | boolean | null | Array<string | number>

/** A date as Y-m-d. The API rejects anything else with a 422. */
export type DateOnly = string

export type StoriesFilter = Partial<Record<StoryFilterableColumn, ColumnFilterValue>> & {
  /** One of station_id or workspace_group_id is required. */
  station_id?: number | number[]
  workspace_group_id?: number
  id?: number | number[]
  master_id?: number | number[]
  /** Range: end_date >= from (or story_date >= from with no end_date); story_date <= to. */
  story_date_from?: DateOnly
  story_date_to?: DateOnly
  /** Exact day; null for undated stories only. */
  story_date?: DateOnly | null
  /** A calendar day in the first station's timezone. */
  created_on?: DateOnly
  updated_on?: DateOnly
  deleted_on?: DateOnly
  /** Inclusive timestamps. */
  created_from?: string
  created_to?: string
  updated_from?: string
  updated_to?: string
  /** only: deleted stories; with: deleted and live. Absent: live only. */
  trashed?: 'only' | 'with'
  /** Exclude shared copies. */
  root_only?: boolean
  /** User ids, user names and role names; any match. */
  assignees?: string | Array<string | number>
  /** Role name to the first assignee's user name; null for "no assignee of that role". */
  assignee_role?: Record<string, string | null>
  created_by_name?: string | null
  /** Platform or format names; any match. */
  assignments?: string | string[]
  assigned_to?: 'me' | number
  pitched_by?: 'me'
  /** Tag names, ILIKE, on the story or its validated media assets. */
  story_tags?: string | string[]
  tag_ids?: number[]
  /** Full-text search; see the endpoint description for the syntax. */
  keywords?: string
  /** keywords becomes a plain substring match. */
  simple_search?: boolean
  date_window?: StoryDateWindow
  include_undated?: boolean
  promoted_now?: boolean
  has_archive_rundowns?: boolean
}

export interface ListStoriesParams {
  filter: StoriesFilter
  /** Comma list or array; leading - for descending. Default -story_date. */
  sort?: string | string[]
  include?: StoryInclude[] | string
  /** 1 to 400, default 100. */
  per_page?: number
  page?: number
}

export interface ListStoryGroupsParams {
  /** A filterable column, story_date, created_on/updated_on/deleted_on, created_by_name, or a role name. */
  group_by: string
  filter: StoriesFilter
  sort?: 'value' | '-value' | 'count' | '-count'
  per_page?: number
  page?: number
}

export interface ListStoryTagsParams {
  filter: StoriesFilter
  per_page?: number
  page?: number
}

export interface StoriesResource {
  /** GET /v3/newsdesk/stories: one page. */
  list(params: ListStoriesParams, options?: RequestOptions): Promise<ListEnvelope<StoryRow>>
  /** Every page of GET /v3/newsdesk/stories, in order. `page` and `per_page` in params are ignored. */
  listAll(params: Omit<ListStoriesParams, 'page' | 'per_page'>, options?: RequestOptions & FetchAllPagesOptions<StoryRow>): Promise<AllPages<StoryRow>>
  /** GET /v3/newsdesk/stories/groups: one row per distinct value of group_by, with a count. */
  groups(params: ListStoryGroupsParams, options?: RequestOptions): Promise<ListEnvelope<StoryGroupRow>>
  /** GET /v3/newsdesk/stories/tags: one page of tag names over the matching stories. */
  tags(params: ListStoryTagsParams, options?: RequestOptions): Promise<ListEnvelope<StoryTagRow>>
  /** Every page of GET /v3/newsdesk/stories/tags. */
  allTags(params: Omit<ListStoryTagsParams, 'page' | 'per_page'>, options?: RequestOptions & FetchAllPagesOptions<StoryTagRow>): Promise<AllPages<StoryTagRow>>
}

export function createStoriesResource(request: RequestFn): StoriesResource {
  const list: StoriesResource['list'] = (params, options) => request<ListEnvelope<StoryRow>>('GET', STORIES_PATH, listQuery(params), options)

  const tags: StoriesResource['tags'] = (params, options) =>
    request<ListEnvelope<StoryTagRow>>('GET', STORY_TAGS_PATH, { filter: filterQuery(params.filter), per_page: params.per_page, page: params.page }, options)

  return {
    list,
    listAll: ({ filter, sort, include }, { perPage, maxPages, onPage, ...options } = {}) =>
      fetchAllPages<StoryRow>((page, per_page) => list({ filter, sort, include, page, per_page }, options), { perPage, maxPages, onPage }),
    groups: (params, options) =>
      request<ListEnvelope<StoryGroupRow>>(
        'GET',
        STORY_GROUPS_PATH,
        { group_by: params.group_by, filter: filterQuery(params.filter), sort: params.sort, per_page: params.per_page, page: params.page },
        options
      ),
    tags,
    allTags: ({ filter }, { perPage, maxPages, onPage, ...options } = {}) =>
      fetchAllPages<StoryTagRow>((page, per_page) => tags({ filter, page, per_page }, options), { perPage, maxPages, onPage })
  }
}

function listQuery({ filter, sort, include, per_page, page }: ListStoriesParams): QueryParams {
  return {
    filter: filterQuery(filter),
    sort: Array.isArray(sort) ? sort.join(',') : sort,
    include: Array.isArray(include) ? include.join(',') : include || undefined,
    per_page,
    page
  }
}

/**
 * The filter object as sent. Values pass through; an empty string stays an
 * empty string because that is the IS NULL form for column filters.
 */
function filterQuery(filter: StoriesFilter): QueryParams {
  const query: QueryParams = {}
  Object.keys(filter).forEach((key) => {
    query[key] = (filter as QueryParams)[key]
  })
  return query
}
